import { IOrderBy } from "../../../../Global/Base/DataSources/Interfaces";
import { Event } from "../../../../Global/Base/Events";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { Control } from "../../../UI.BaseControls/Control";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IDataGrid } from "../../IDataGrid";
import { Column } from "../Columns/Column";
import { IDataRowElement } from "./IDataRow";
import { IHeaderRow } from "./IHeaderRow";

export class GroupingTreeHeaderRow extends Control implements IHeaderRow<IDataRowElement> {
    //#region Events
    /** Udalost zavreni grupovaciho panelu */
    public close: Event<void, void>;
    //#endregion Events

    //#region Fields
    private _closeButton: HTMLElement;
    /** Umožňuje aktualizovat text v hlavičce a její tooltip v průběhu odebírání a přidávání sloupců */
    private _localizationParts: Array<ColumnLocalization>;
    //#endregion Fields

    //#region Methods
    public render(_owner: IDataGrid<IDataRowElement>, _columns: Array<Column>, _orderBy: IOrderBy[]): void {
        // Prazdna metoda
    }

    /**
     * Vloží do hlavičky span s dalším textem
     * @param locKey Lokalizační klíč s textem, který má být zobrazen
     */
    public async addTextElement(attrName: string, locKey: string, locParams?: string[]): Promise<void> {
        const text = await Localization.getTextPromiseWithParams(locKey, locParams);
        const element = createElement({ type: "span", attrs: { "data-lockey": locKey } });
        element.innerText = text;

        this._localizationParts.push({
            attrName: attrName,
            element: element,
            locKey: locKey,
            locParams: locParams,
        });

        this._updateTitle(text, this._localizationParts.length - 1);

        this.appendChild(element);
    }

    /**
     * Aktualizuje hlavičku po odebrání sloupce
     * @param locKey Klíč sloupce, který je odebírán
     */
    public removeTextElement(attrName: string, locKey: string): void {
        const headerPart = this._localizationParts.find((item) => item.locKey === locKey && StringUtils.compareCaseInsensitive(item.attrName, attrName));

        this.removeChild(headerPart.element);

        this._localizationParts.splice(this._localizationParts.indexOf(headerPart), 1);

        this.title = ""; // Titulek je souvislý kus textu, který je třeba celý přegenerovat

        this._localizationParts.forEach((item, index) => {
            this._updateTitle(item.element.textContent, index);
        });
    }

    /**
     * Aktualizuje tooltip hlavičky podle aktuálně zobrazených sloupců
     * @param text Text k přidání do tooltipu
     * @param index Index vkládaného prvku, který slouží k rozpoznání, zda se mají vkládat čárky
     */
    private _updateTitle(text: string, index: number): void {
        this.title = `${this.title ? this.title : ""}${index > 1 ? "," : ""} ${text}`; // `
    }
    //#endregion Methods

    //#region HTMLElement
    protected override _constructor(): void {
        super._constructor();

        this._localizationParts = [];
        this.close = new Event<void, void>();

        this.classList.add("header-row");

        this._eventsController.add(Localization.languageChanged, async () => { // Vzhledem ke skládání tooltipu z několika lokalizací reagujeme na změnu jazyka takto
            const resultsPromises = this._localizationParts.map<Promise<{ item: ColumnLocalization; text: string }>>(async item => ({
                item: item,
                text: await Localization.getTextPromiseWithParams(item.locKey, item.locParams),
            }));
            const results = await Promise.all(resultsPromises);

            this.title = ""; // Titulek je souvislý kus textu, který je třeba celý přegenerovat

            results.forEach((result, index) => {
                result.item.element.textContent = result.text;
                this._updateTitle(result.text, index);
            });
        });

        this._closeButton = createElement({ type: "button", classes: "btn flat icon close" });
        Localization.addLocDescData(this._closeButton, "GUI-280072", "title");//Zrušit seskupení
        this._closeButton.onclick = (() => {
            if (this._closeButton.classList.contains("Disabled"))
                return;
            this.close.trigger(null, null);
        });

        this.appendChild(this._closeButton);
    }

    //#endregion HTMLElement
}

/** Udržuje lokalizované elementy s jejich klíči pro skládání tooltipu */
export interface ColumnLocalization {
    /** Atribut */
    attrName: string;
    /** Lokalizační klíč */
    locKey: string;
    /** Parametry lokalizačního textu */
    locParams?: string[];
    /** Element k lokalizaci */
    element: HTMLElement;
}

registerCustomElement("tsc-GroupingTreeHeaderRow", GroupingTreeHeaderRow);
